import { useAppDispatch } from "hooks/hooks";
import { useEffect, useRef, useState } from "react";
import {
  getResourcesList,
  selectState,
  setNewResourcePopover,
  setReversPopover,
  setSearchResourcesPopover,
  getResourcesCensusForPrint,
  resetCensusAfterPrint,
  getResourceStatusForPrint,
  resetStatusAfterPrint,
} from "../main.slice";
import { selectState as selectStateAuth } from "authentication/main.slice";
import {
  selectState as selectStateCharges,
  setResourceDischargePopover,
} from "../../Charges/charges.slice";
import NewResourcePopover from "../../Main/components/NewResourcePopover";
import PrintResourcesStatus from "./PrintResourcesStatus";
import SearchResourcesPopover from "./SearchResourcesPopover";
import ToolboxButton, {
  ToolboxButtonIcon,
} from "Dashboard/components/Buttons/ToolboxButton";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Popover from "components/Popover/Popover";
import "../../../styles/popover.scss";
import ReversPopover from "./ReversPopover";
import {
  Control,
  useForm,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetFocus,
  UseFormSetValue
} from "react-hook-form";
import { ICreateResource } from "Dashboard/scenes/Resources/domain/ICreateResource";
import PrintResourcesCensus from "./PrintResourcesCensus";
import { IGetResourcesListRequestParams } from "Dashboard/scenes/Resources/domain/IGetResourcesListRequestParams";
import { SearchType } from "Dashboard/domain/SearchType";
import ResourceDischargePopover from "../../Charges/components/ResourceDischargePopover";
import { hasWriteAccessTo } from "utils/authUtil";
import { RESOURCES_MODULE_NAME } from "Dashboard/scenes/Resources/domain/ResourcesModule";
import { usePagePrint } from "hooks/usePagePrint";
import { areQueryParamsEmpty } from "utils/queryParamsUtil";
import { IReversCharge } from "Dashboard/scenes/Resources/domain/IReversCharge";

interface IProps {
  handleSubmitSearch: UseFormHandleSubmit<IGetResourcesListRequestParams>;
  registerSearch: UseFormRegister<IGetResourcesListRequestParams>;
  controlSearch: Control<IGetResourcesListRequestParams, any>;
  getValuesSearch: UseFormGetValues<IGetResourcesListRequestParams>;
  resetSearch: UseFormReset<IGetResourcesListRequestParams>;
  setFocusSearch: UseFormSetFocus<IGetResourcesListRequestParams>;
  setValueSearch: UseFormSetValue<IGetResourcesListRequestParams>;
};

const Toolbox = (props: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const componentRef = useRef<HTMLDivElement>(null);
  const componentPrintCensusRef = useRef<HTMLDivElement>(null);
  const {
    reversPopover,
    newResourcePopover,
    searchPopover,
    resourceStatus,
    resourcesCensus
  } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);
  const {
    handleSubmitSearch,
    registerSearch,
    controlSearch,
    getValuesSearch,
    resetSearch,
    setFocusSearch,
    setValueSearch
  } = props;

  const {
    dischargingResourcePopover
  } = useSelector(selectStateCharges);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm<ICreateResource>();

  const [searchTypeValue, setSearchTypeValue] = useState(SearchType.BASIC);
  const [pendingChargeToDelete, setPendingChargeToDelete] = useState<IReversCharge | undefined>(undefined);

  const requestPrintingStatus = (printAllResources: boolean) => {
    const searchParams = getValuesSearch();
    if (printAllResources) {
      dispatch(getResourceStatusForPrint({ showRefuelsAtDate: searchParams.showRefuelsAtDate }));
    } else {
      dispatch(getResourceStatusForPrint(searchParams));
    }
  };

  const isSearchFormEmpty = areQueryParamsEmpty(getValuesSearch(), ["showRefuelsAtDate"]);

  const handlePrintStatus = usePagePrint(
    componentRef, { margins: "0.5cm" });

  const requestPrintingCensus = () => {
    dispatch(getResourcesCensusForPrint());
  };

  const handlePrintCensus = usePagePrint(
    componentPrintCensusRef, { margins: "0.5cm" });

  useEffect(() => {
    if (resourceStatus) {
      handlePrintStatus();
      dispatch(resetStatusAfterPrint());
    }
  }, [resourceStatus, handlePrintStatus, dispatch]);

  useEffect(() => {
    if (resourcesCensus) {
      handlePrintCensus();
      dispatch(resetCensusAfterPrint());
    }
  }, [resourcesCensus, handlePrintCensus, dispatch]);

  return (
    <nav className="container">
      <div>
        <ToolboxButton
          icon={ToolboxButtonIcon.Back}
          tooltip="Glavni meni"
          toolbox={true}
          onClick={() => navigate('/dashboard')}
        />
      </div>
      <div>
        <ToolboxButton
          icon={ToolboxButtonIcon.Add}
          tooltip="Novi resurs"
          toolbox={true}
          onClick={hasWriteAccessTo(user, RESOURCES_MODULE_NAME)
            ? () => dispatch(setNewResourcePopover({ isOpen: true }))
            : undefined}
        />
        <ToolboxButton
          icon={ToolboxButtonIcon.Search}
          tooltip="Traži"
          toolbox={true}
          onClick={() => {
            dispatch(setSearchResourcesPopover({ isOpen: true }));
          }}
        />
        {!isSearchFormEmpty
          && (<ToolboxButton
            icon={ToolboxButtonIcon.CancelSearch}
            tiny={true}
            tooltip="Pokaži sve"
            onClick={() => {
              dispatch(getResourcesList({
                showRefuelsAtDate: getValuesSearch("showRefuelsAtDate")
              }));
              resetSearch();
            }}
          />)}
        <ToolboxButton
          icon={ToolboxButtonIcon.DocumentText}
          tooltip="Štampaj revers"
          toolbox={true}
          onClick={hasWriteAccessTo(user, RESOURCES_MODULE_NAME)
            ? () => dispatch(setReversPopover({ isOpen: true }))
            : undefined}
        />
        <ToolboxButton
          icon={ToolboxButtonIcon.Print}
          tooltip="Štampa resursa na dan"
          toolbox={true}
          onClick={() => requestPrintingStatus(true)}
        />
        <ToolboxButton
          icon={ToolboxButtonIcon.Print}
          tooltip="Štampa resursa po izboru"
          toolbox={true}
          onClick={() => requestPrintingStatus(false)}
        />
        <ToolboxButton
          icon={ToolboxButtonIcon.Print}
          tooltip="Štampa popisa resursa"
          toolbox={true}
          onClick={requestPrintingCensus}
        />
      </div>
      <div ref={componentRef} className="print-area">
        <PrintResourcesStatus />
      </div>
      <div ref={componentPrintCensusRef} className="print-area">
        <PrintResourcesCensus />
      </div>
      <Popover
        isOpen={newResourcePopover.isOpen}
        onClose={() => dispatch(setNewResourcePopover({ isOpen: false }))}
        title="Novi Resurs"
        direction="right"
        arrowStyle={{ top: "42px" }}
        position={{ top: 10, left: 185 }}
      >
        <NewResourcePopover
          handleSubmit={handleSubmit}
          register={register}
          control={control}
          errors={errors}
          reset={reset}
          watch={watch}
        />
      </Popover>
      <Popover
        isOpen={searchPopover.isOpen}
        onClose={() => {
          dispatch(setSearchResourcesPopover({ isOpen: false }));
        }}
        direction="right"
        arrowStyle={{ top: "42px" }}
        title="Pretraga"
        position={{ top: 10, left: 250 }}
      >
        <SearchResourcesPopover
          handleSubmit={handleSubmitSearch}
          register={registerSearch}
          control={controlSearch}
          getValues={getValuesSearch}
          setValue={setValueSearch}
          searchType={searchTypeValue}
          setSearchType={setSearchTypeValue}
          setFocus={setFocusSearch}
        />
      </Popover>
      <Popover
        isOpen={reversPopover.isOpen}
        onClose={() => {
          if (pendingChargeToDelete === undefined) {
            dispatch(setReversPopover({ isOpen: false }));
          }
        }}
        title="Štampa Reversa"
        direction="right"
        arrowStyle={{ top: "42px" }}
        position={{ top: 10, left: 310 }}
      >
        <ReversPopover pendingChargeToDelete={pendingChargeToDelete}
          setPendingChargeToDelete={setPendingChargeToDelete} />
      </Popover>
      <Popover
        isOpen={dischargingResourcePopover.isOpen}
        onClose={() => {
          dispatch(setResourceDischargePopover({ isOpen: false }));
        }}
        title="Razduženje"
        direction="right"
        arrowStyle={{ top: "50%" }}
        position={{
          top: dischargingResourcePopover.top,
          left: dischargingResourcePopover.left,
          offsetTop: -90,
          offsetLeft: 15,
        }}
      >
        <ResourceDischargePopover />
      </Popover>
    </nav>
  );
};

export default Toolbox;
